import { Navigate, type RouteObject } from 'react-router'

import { RouterErrorBoundary } from './components/ErrorBoundary'
import { isProduction } from './config/config'
import { INSURANCE_VERTICAL_PATHS } from './config/insuranceVerticals'
import { Product } from './config/product'
import { routeComponents } from './lib/routeComponents'
import { ONBOARDING_PATH, PAGE_COMPONENTS, pageSegment } from './routes/Bookkeeper/pages'
import {
  BookkeeperNotFound,
  BookkeeperRouteError,
  LegacyRedirect,
  WorkspacePage,
} from './routes/Bookkeeper/routeElements'
import { CAI_AGENT_LOGIN_PATH, CAI_AGENT_PATH } from './routes/CaiAgent/api'
import { CaiAgentRouteError } from './routes/CaiAgent/CaiAgentError'
import { MovedToInsure, OnTheAppHost } from './routes/MovedToInsure'
import { Root } from './routes/Root'

const insure = routeComponents(Product.Insure, () =>
  import('./routes/insurePages').then((module) => module.insurePages)
)
const bookkeeper = routeComponents(Product.Bookkeeper, () =>
  import('./routes/Bookkeeper/bookkeeperPages').then((module) => module.bookkeeperPages)
)

const caiAgent = routeComponents(Product.CaiAgent, () =>
  import('./routes/CaiAgent/caiAgentPages').then((module) => module.caiAgentPages)
)

/**
 * Every route in the app.
 *
 * /bookkeeper is Bookkeeper's landing page on the marketing site and Bookkeeper itself everywhere
 * else, so the marketing site routes none of Bookkeeper's pages.
 */
export function createRoutes(onMarketingSite: boolean): RouteObject[] {
  return [
    {
      path: '/',
      Component: Root,
      children: [
        {
          ...insure('InsureFrame'),
          errorElement: <RouterErrorBoundary />,
          children: insureRoutes(onMarketingSite),
        },
        ...(onMarketingSite
          ? []
          : [
              {
                ...bookkeeper('BookkeeperFrame'),
                errorElement: <BookkeeperRouteError />,
                children: bookkeeperRoutes(),
              },
              {
                ...caiAgent('CaiAgentFrame'),
                errorElement: <CaiAgentRouteError />,
                children: caiAgentRoutes(),
              },
            ]),
      ],
    },
  ]
}

function insureRoutes(onMarketingSite: boolean): RouteObject[] {
  return [
    {
      ...insure('MarketingLayout'),
      handle: { prerender: true },
      children: [
        { index: true, ...insure('Home') },
        { path: 'insurance', ...insure('MarketingInsurance') },
        { path: 'group-captive', ...insure('GroupCaptive') },
        { path: 'how-we-use-ai', ...insure('HowWeUseAi') },
        { path: 'privacy', ...insure('Privacy') },
        { path: 'terms', ...insure('Terms') },
      ],
    },

    {
      // Read-only quotes page, shared by link. Public: no PrivateLayout, and the
      // endpoint behind it is unauthenticated by design.
      path: 'quotes/:applicationId',
      ...insure('PublicQuotes'),
    },
    {
      // One-off standalone FuelApp landing page (moved from HubSpot's
      // cai.ai/fuelapp) — ships its own header/footer, no MarketingLayout.
      path: 'fuelapp',
      ...insure('FuelApp'),
      handle: { prerender: true },
    },
    // Off the marketing site, /bookkeeper is Bookkeeper itself.
    ...(onMarketingSite
      ? [
          {
            // One-off standalone Bookkeeper landing page — own header/footer, no MarketingLayout.
            path: 'bookkeeper',
            ...insure('BookkeeperLanding'),
            handle: { prerender: true },
          },
        ]
      : []),
    {
      // Renamed from /accountant; the CDN serves a 301 (see public/_redirects) and this covers client-side navigations.
      path: 'accountant',
      element: <Navigate to="/bookkeeper" replace />,
    },
    {
      // One-off standalone Cai AI CFO beta landing page — own header/footer, no MarketingLayout.
      path: 'cfo',
      ...insure('AiCfo'),
      handle: { prerender: true },
    },
    {
      // Standalone large-fleet insurance landing page — own header/footer, no MarketingLayout, paid-campaign destination.
      path: 'insurance/large-fleet-coverage',
      ...insure('LargeFleets'),
      handle: { prerender: true },
    },
    // Standalone contractor-trade insurance landing pages (cai.ai/insurance/hvac, …) — own header/footer, no MarketingLayout.
    ...INSURANCE_VERTICAL_PATHS.map((path) => ({
      path: path.slice(1),
      ...insure('InsuranceVertical'),
      handle: { prerender: true },
    })),
    {
      // Standalone Amazon Relay insurance landing page — own header/footer, no MarketingLayout, paid-campaign destination.
      path: 'insurance/amazon-relay',
      ...insure('AmazonRelay'),
      handle: { prerender: true },
    },
    {
      // One-off standalone FuelApp referral-program landing page — ships
      // its own header/footer, no MarketingLayout.
      path: 'fuelapp/referrals',
      ...insure('FuelAppReferral'),
      handle: { prerender: true },
    },
    {
      // One-off standalone FuelApp Teams private-beta landing page — ships
      // its own header/footer, no MarketingLayout.
      path: 'fuelapp/teams',
      ...insure('FuelAppTeams'),
      handle: { prerender: true },
    },
    // app private routes
    {
      path: '/',
      ...insure('PrivateLayout'),
      children: [
        // routes without sidebar
        {
          path: '/',
          ...insure('NavigationLayout'),
          children: [
            {
              path: 'applications/:appId/sign-bor',
              ...insure('SignBor'),
            },
            {
              path: 'sign-envelope/:envelopeId',
              ...insure('SignEnvelope'),
            },
            {
              path: 'applications/:appId/upload-loss-runs',
              ...insure('UploadLossRuns'),
            },
          ],
        },
        // one time-to-quoting clock runs across the chat and its quotes page
        {
          ...insure('TimeToQuotingLayout'),
          children: [
            // insurance application chat — ships its own ProductFlowNav, no shared nav/sidebar
            {
              path: '/application',
              ...insure('InsuranceApplication'),
            },
            // the application's review, which /application hands off to at the chat's review step
            {
              path: '/insurance/confirm-info',
              ...insure('ConfirmInfoPage'),
            },
            // the application's quotes page, which /application hands off to once carriers are asked
            {
              path: '/quotes',
              ...insure('InsuranceQuotes'),
            },
          ],
        },
        // accept invitation - no sidebar or navigation
        {
          path: 'insure/accept-invitation/:id',
          ...insure('AcceptInvitation'),
        },
        // CAI-staff-only design-system reference — no app chrome.
        // Auth-gated in production only; dev/staging expose it publicly below.
        ...(isProduction
          ? [
              { path: 'design-system', ...insure('DesignSystem') },
              { path: 'design-system/frames/nav', ...insure('NavFrame') },
              { path: 'design-system/frames/account-banner', ...insure('AccountBannerFrame') },
              { path: 'design-system/frames/claim', ...insure('ClaimFrame') },
              { path: 'design-system/prototypes/quote-card', ...insure('QuoteCardPrototypePage') },
              { path: 'design-system/prototypes/quotes-page', ...insure('QuotesPagePrototype') },
              {
                path: 'design-system/prototypes/confirm-info-cards-v1-0',
                ...insure('ConfirmInfoCardsPrototypePage'),
              },
              {
                path: 'design-system/prototypes/account-banners-v1-0',
                ...insure('AccountBannersPrototypePage'),
              },
              {
                path: 'design-system/prototypes/account-side-nav-v1-0',
                ...insure('AccountSideNavPrototypePage'),
              },
              {
                path: 'design-system/prototypes/claims-v1-0',
                ...insure('ClaimsPrototypePage'),
              },
              {
                path: 'design-system/prototypes/ai-positioning-v1-0',
                ...insure('AiPositioningPrototypePage'),
              },
              {
                path: 'design-system/prototypes/cai-mvp-v1-0',
                ...insure('CaiMvpFullPage'),
              },
              {
                path: 'design-system/prototypes/checkout-manager-v2',
                ...insure('CheckoutManagerPrototypePage'),
              },
              { path: 'design-system/conversation-tree', ...insure('ConversationTreePage') },
            ]
          : []),
      ],
    },
    ...(isProduction
      ? []
      : [
          { path: 'design-system', ...insure('DesignSystem') },
          { path: 'design-system/frames/nav', ...insure('NavFrame') },
          { path: 'design-system/frames/account-banner', ...insure('AccountBannerFrame') },
          { path: 'design-system/frames/claim', ...insure('ClaimFrame') },
          { path: 'design-system/prototypes/quote-card', ...insure('QuoteCardPrototypePage') },
          { path: 'design-system/prototypes/quotes-page', ...insure('QuotesPagePrototype') },
          {
            path: 'design-system/prototypes/confirm-info-cards-v1-0',
            ...insure('ConfirmInfoCardsPrototypePage'),
          },
          {
            path: 'design-system/prototypes/account-banners-v1-0',
            ...insure('AccountBannersPrototypePage'),
          },
          {
            path: 'design-system/prototypes/account-side-nav-v1-0',
            ...insure('AccountSideNavPrototypePage'),
          },
          {
            path: 'design-system/prototypes/claims-v1-0',
            ...insure('ClaimsPrototypePage'),
          },
          {
            path: 'design-system/prototypes/ai-positioning-v1-0',
            ...insure('AiPositioningPrototypePage'),
          },
          {
            path: 'design-system/prototypes/cai-mvp-v1-0',
            ...insure('CaiMvpFullPage'),
          },
          {
            path: 'design-system/prototypes/checkout-manager-v2',
            ...insure('CheckoutManagerPrototypePage'),
          },
          { path: 'design-system/conversation-tree', ...insure('ConversationTreePage') },
        ]),
    // sign-up (public, no app chrome): the sign-in panel under a "create your account" heading
    {
      path: 'sign-up',
      ...insure('SignUpPage'),
    },
    // login token route (public, used in incognito)
    {
      path: 'login-token',
      ...insure('Impersonate'),
    },
    // the application chat's front door: builds a stub account and signs it into /application
    {
      path: 'application/start',
      ...insure('StartApplication'),
    },
    // the same door for contractors (HVAC and other trades), flag or not
    {
      path: 'application/workers/start',
      ...insure('StartApplication'),
    },
    // embed demo (public, used in iframe embed examples + e2e)
    {
      path: 'embed-demo',
      ...insure('EmbedDemo'),
    },
    // public web DOT funnel — ProductFlowLayout adds the shared nav + legal footer.
    // GetEstimatesFlow serves both URLs as one continuously-mounted page — it
    // navigates itself from insurance/start to insurance/estimates/:id once a
    // funnel session exists.
    {
      ...insure('ProductFlowLayout'),
      children: [
        {
          path: 'insurance/start',
          ...insure('GetEstimatesFlow'),
        },
        {
          path: 'insurance/start/new-authority',
          ...insure('NewAuthorityStart'),
        },
        {
          path: 'insurance/estimates/:id',
          ...insure('GetEstimatesFlow'),
        },
      ],
    },
    // large-fleet packet upload — same layout mechanism, dark theme to match the page's design.
    {
      ...insure('DarkProductFlowLayout'),
      children: [
        {
          path: 'insurance/large-fleets',
          ...insure('LargeFleetPage'),
        },
        {
          path: 'insurance/large-fleets/confirm',
          ...insure('LargeFleetConfirmPage'),
        },
      ],
    },
    {
      path: 'claims/:claimId/photos',
      ...insure('ClaimPhotosPage'),
    },
    // QA: create a fresh test user and sign them in (disabled in prod)
    ...(isProduction
      ? []
      : [
          {
            path: 'new/user',
            ...insure('CreateTestUser'),
          },
          {
            // QA: create a fresh CAI-admin test user (seeded fleet) and sign
            // them in — used by the authenticated visual-capture e2e suite.
            path: 'new/admin',
            ...insure('CreateTestAdmin'),
          },
        ]),
    // Insure's app moved from /app to /insure.
    { path: 'app/*', element: <MovedToInsure /> },
    // The app itself sits in the product shell, which the marketing site doesn't route.
    ...(onMarketingSite ? [{ path: 'insure/*', element: <OnTheAppHost /> }] : []),
    // 404 catch-all route
    { path: '*', ...insure('NotFound') },
  ]
}

/** Insure's signed-in app at /insure: in Insure's own frame, or inside the shared product shell. */
function insureAppRoute(): RouteObject {
  return {
    path: 'insure',
    children: [
      {
        // Entry point for "/insure": decide the right landing page for the
        // authenticated user (onboarding, admin console, policy, etc.).
        // Deliberately OUTSIDE AppLayout — nesting it inside painted the
        // sidebar/nav/onboarding banner before the destination was known,
        // so a user bound for /application saw a flash of app chrome that
        // was then torn down.
        index: true,
        ...insure('IndexRedirect'),
      },
      // routes with sidebar
      {
        ...insure('AppLayout'),
        children: [
          // routes accessible to all users
          {
            path: 'insurance',
            ...insure('Insurance'),
          },
          { path: 'dashboard', element: <Navigate to="/insure/insurance" replace /> },
          {
            path: 'admin-settings',
            ...insure('AdminSettings'),
          },
          {
            path: 'certificates',
            ...insure('Certificates'),
          },
          // routes restricted to admin/business owner
          {
            ...insure('AdminLayout'),
            children: [
              {
                path: 'claims',
                ...insure('Claims'),
              },
              {
                path: 'claims/new',
                ...insure('NewClaim'),
              },
              {
                // Revisit a draft claim to keep editing it.
                path: 'claims/:claimId',
                ...insure('EditClaim'),
              },
              {
                path: 'policy-changes',
                ...insure('PolicyChanges'),
              },
              {
                path: 'policy-changes/new',
                ...insure('NewPolicyChange'),
              },
              {
                // A draft opens in the guided form, anything else on where it stands.
                path: 'policy-changes/:requestId',
                ...insure('PolicyChangePage'),
              },
              {
                // A request the broker sent back for changes.
                path: 'policy-changes/:requestId/update',
                ...insure('PolicyChangeUpdatePage'),
              },
              {
                path: 'violations',
                ...insure('Violations'),
              },
              {
                path: 'violations/:id',
                ...insure('ViolationDetail'),
              },
              {
                path: 'documents',
                ...insure('Documents'),
              },
              {
                path: 'vault',
                ...insure('VaultPage'),
                children: [
                  { index: true, ...insure('VaultFolders') },
                  // The folders these listed now render on the front page; old links still work.
                  { path: 'drivers', element: <Navigate to="/insure/vault" replace /> },
                  { path: 'equipment', element: <Navigate to="/insure/vault" replace /> },
                  { path: 'carrier', ...insure('CarrierFilePage') },
                  { path: 'drivers/:entityId', ...insure('DriverFilePage') },
                  { path: 'equipment/trucks/:entityId', ...insure('TruckFilePage') },
                  { path: 'equipment/trailers/:entityId', ...insure('TrailerFilePage') },
                  { path: 'timeline', ...insure('TimelinePage') },
                ],
              },
              {
                path: 'drivers',
                ...insure('Drivers'),
              },
              {
                path: 'trucks',
                ...insure('Trucks'),
              },
              {
                path: 'trailers',
                ...insure('Trailers'),
              },
              {
                path: 'fleet-details',
                ...insure('FleetDetails'),
              },
            ],
          },
          {
            path: 'internal',
            ...insure('InternalLayout'),
            children: [
              {
                path: 'customers',
                ...insure('Customers'),
              },
              {
                path: 'customers/:id',
                ...insure('CustomerDetail'),
              },
              {
                // A tab keeps its own place after its segment: /vault/equipment/trucks/<id>.
                path: 'customers/:id/*',
                ...insure('CustomerDetail'),
              },
              {
                path: 'qa',
                ...insure('QA'),
              },
              {
                path: 'qa/geico',
                ...insure('GeicoQa'),
              },
              {
                path: 'qa/emails',
                ...insure('QaEmails'),
              },
              {
                path: 'applications',
                ...insure('Applications'),
              },
              {
                path: 'applications/:id',
                ...insure('ApplicationDetail'),
              },
              {
                path: 'requotes',
                ...insure('Requotes'),
              },
              {
                path: 'claims',
                ...insure('InternalClaims'),
              },
              {
                // CAI staff creating a claim on behalf of a customer's business owner.
                path: 'claims/new',
                ...insure('NewClaim'),
              },
              {
                // Revisit a draft claim to keep editing it.
                path: 'claims/:claimId',
                ...insure('EditClaim'),
              },
              {
                path: 'coi-generator',
                ...insure('CoiGenerator'),
              },
              {
                path: 'coi-generator/:id',
                ...insure('CoiGeneratorDetail'),
              },
              {
                path: 'cab',
                ...insure('Cab'),
              },
              {
                path: 'cab/:dotNumber',
                ...insure('Cab'),
              },
              {
                path: 'vin',
                ...insure('Vin'),
              },
              {
                path: 'vin/:vin',
                ...insure('Vin'),
              },
              {
                path: 'agent-tools',
                ...insure('AgentTools'),
              },
              {
                path: 'agent-chat',
                ...insure('AgentChat'),
              },
              {
                path: 'agent-chat/:id',
                ...insure('AgentChat'),
              },
              {
                path: 'staff-users',
                ...insure('StaffUsers'),
              },
              {
                path: 'staff-users/:id',
                ...insure('StaffUserDetail'),
              },
              {
                path: 'endorsements',
                ...insure('InternalEndorsements'),
              },
              {
                path: 'endorsements/:requestId',
                ...insure('InternalEndorsements'),
              },
              {
                path: 'pending-equipment-changes',
                ...insure('PendingEquipmentChanges'),
              },
              {
                path: 'coi-requests',
                ...insure('CoiRequests'),
              },
              {
                path: 'reminders',
                ...insure('RemindersPage'),
              },
              {
                path: 'carriers',
                ...insure('Carriers'),
              },
              {
                path: 'corgi-connection',
                ...insure('CorgiConnection'),
              },
              {
                path: 'sales-leads',
                ...insure('SalesLeads'),
              },
              {
                // Static segment must precede the ':id' route below.
                path: 'sales-leads/pipeline',
                ...insure('PipelineBoard'),
              },
              {
                path: 'sales-leads/:id',
                ...insure('SalesLeads'),
              },
              {
                path: 'work-queue',
                ...insure('WorkQueue'),
              },
              {
                path: 'my-pipeline',
                ...insure('MyPipeline'),
              },
              {
                path: 'email-templates',
                ...insure('EmailTemplatesIndex'),
              },
              {
                path: 'sequence-templates',
                ...insure('SequenceTemplatesIndex'),
              },
              {
                path: 'sequences',
                ...insure('SequencesIndex'),
              },
              {
                path: 'sequences/:id',
                ...insure('SequenceDetail'),
              },
              {
                path: 'email-reporting',
                ...insure('EmailReportingIndex'),
              },
              {
                path: 'agent-runs',
                ...insure('AgentReporting'),
              },
              {
                path: 'emails',
                ...insure('EmailsIndex'),
              },
              {
                path: 'quotes-analytics',
                ...insure('QuotesAnalytics'),
              },
              {
                path: 'sales-analytics',
                ...insure('SalesAnalytics'),
              },
              {
                path: 'calls',
                ...insure('CallsIndex'),
              },
              {
                path: 'sms',
                ...insure('SmsIndex'),
              },
              {
                path: 'sales/insurance-estimator',
                ...insure('InsuranceEstimator'),
              },
              {
                path: 'sales/insurance-estimator/:dotNumber',
                ...insure('InsuranceEstimator'),
              },
            ],
          },
        ],
      },
    ],
  }
}

function bookkeeperRoutes(): RouteObject[] {
  return [
    {
      ...bookkeeper('PublicShell'),
      children: [
        { path: '/bookkeeper/login', ...bookkeeper('AuthPage') },
        { path: ONBOARDING_PATH, ...bookkeeper('OnboardingPage') },
      ],
    },
  ]
}

function caiAgentRoutes(): RouteObject[] {
  return [
    { path: CAI_AGENT_LOGIN_PATH, ...caiAgent('CaiSignInPage') },
    {
      ...caiAgent('CaiAgentShell'),
      children: [
        {
          ...caiAgent('CaiAgentLayout'),
          children: [
            { path: '/cai-agent', element: null },
            { path: '/cai-agent/ideas', element: null },
            { path: '/cai-agent/connect', element: null },
            { path: '/cai-agent/memories', element: null },
            { path: '/cai-agent/tasks', element: null },
            { path: '/cai-agent/*', element: <Navigate to={CAI_AGENT_PATH} replace /> },
            { path: '/fuel', ...caiAgent('FuelOffer') },
            // Insure's app sits in the shell beside Cai and Bookkeeper, so moving between them never loads the page again.
            {
              ...insure('InsureFrame'),
              children: [{ ...insure('PrivateLayout'), children: [insureAppRoute()] }],
            },
            {
              ...bookkeeper('BookkeeperFrame'),
              children: [
                {
                  path: '/bookkeeper',
                  ...bookkeeper('SignedInShell'),
                  children: [
                    {
                      ...bookkeeper('SignedInProductFlow'),
                      children: [
                        { path: 'businesses', ...bookkeeper('NewBusinessPage') },
                        { path: 'demo', ...bookkeeper('ScheduleDemoPage') },
                        {
                          ...bookkeeper('DemoGate'),
                          children: [{ path: 'payment', ...bookkeeper('PaymentPage') }],
                        },
                      ],
                    },
                    {
                      ...bookkeeper('BillingGate'),
                      children: [
                        {
                          ...bookkeeper('SignedInProductFlow'),
                          children: [
                            // Outside OnboardingGate, which redirects customers to these steps.
                            { path: 'schedule', ...bookkeeper('ScheduleCallPage') },
                            { path: 'connect', ...bookkeeper('ConnectDataPage') },
                          ],
                        },
                        {
                          ...bookkeeper('OnboardingGate'),
                          children: [
                            {
                              ...bookkeeper('BookkeeperLayout'),
                              children: [
                                {
                                  path: 'accounts',
                                  element: <LegacyRedirect pathname="/bookkeeper/transactions" />,
                                },
                                ...Object.keys(PAGE_COMPONENTS).map((url) => {
                                  const segment = pageSegment(url)
                                  return segment
                                    ? { path: segment, element: <WorkspacePage url={url} /> }
                                    : { index: true, element: <WorkspacePage url={url} /> }
                                }),
                                { path: '*', element: <BookkeeperNotFound /> },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ]
}
